/**
 * The one filter control over the survey column — and the three facets behind it.
 *
 * ── One button, never a row of chips ─────────────────────────────────────────────────────────
 * Owner, 16 September 2026: „za dużo się dzieje". So the facets live in a popup, and the button
 * carries a figure when any of them is on: a narrowed list must never look like an empty one.
 *
 * ── The popup STAYS OPEN, and that is Base UI's own default here ─────────────────────────────
 * `Menu.RadioItem` defaults `closeOnClick` to FALSE, because a radio group is often a multi-step
 * filter — which is exactly what this is. Three choices in one visit is the ordinary use, so nothing
 * overrides it. The single-choice pickers in the plan builder do override it, and their comment says
 * why; this is the case the default was written for.
 *
 * ── Each group's label sits INSIDE its group ─────────────────────────────────────────────────
 * Base UI reads a `Menu.GroupLabel`'s group off context and THROWS — "MenuGroupContext is missing" —
 * when one stands beside a `RadioGroup` rather than within it. That took the plan builder down on
 * every open, and it is recorded here rather than rediscovered.
 */

import { Menu } from '@base-ui/react/menu';
import { useTranslation } from 'react-i18next';

import { FilterIcon } from '~/shared/ui/FilterIcon';
import menuStyles from '~/shared/ui/MenuSurface.module.css';

import {
  NO_SURVEY_FACETS,
  activeFacetCount,
  type CadenceFacet,
  type FacetClient,
  type StatusFacet,
  type SurveyFacets,
} from '../model/surveyFilters';
import canon from '~/shared/ui/surveyCanon.module.css';

const STATUSES: readonly StatusFacet[] = ['all', 'active', 'paused'];
const CADENCES: readonly CadenceFacet[] = ['all', 'Day', 'Week', 'Month'];

/** The facet value → its key under `web:reports.surveys.filters`. A table, so a new value is a compile error. */
const STATUS_KEY: Record<StatusFacet, string> = {
  all: 'statusAll',
  active: 'statusActive',
  paused: 'statusPaused',
};

const CADENCE_KEY: Record<CadenceFacet, string> = {
  all: 'cadenceAll',
  Day: 'cadenceDay',
  Week: 'cadenceWeek',
  Month: 'cadenceMonth',
};

/** The client facet's value is a number, and a radio group's is a string. One spelling, both ways. */
const ANY_CLIENT = 'all';

interface SurveyFilterMenuProps {
  readonly facets: SurveyFacets;
  readonly onChange: (facets: SurveyFacets) => void;
  readonly clients: readonly FacetClient[];
  /** Dead while there is nothing to narrow — a pending or failed read. */
  readonly disabled: boolean;
}

export function SurveyFilterMenu({ facets, onChange, clients, disabled }: SurveyFilterMenuProps) {
  const { t } = useTranslation();
  const active = activeFacetCount(facets);
  // The figure rides in the NAME rather than only in a badge: the button is icon-only, so its
  // accessible name is the only place a listener can learn that the list is narrowed.
  const label =
    active === 0
      ? t('web:reports.surveys.filters.trigger')
      : t('web:reports.surveys.filters.triggerActive', { count: active });

  return (
    <Menu.Root>
      <Menu.Trigger
        className={`${String(canon.ibtn)} ${String(canon.ibtnMd)} ${
          active === 0 ? '' : String(canon.ibtnOn)
        }`}
        aria-label={label}
        title={label}
        disabled={disabled}
      >
        <FilterIcon className={canon.ibtnIconMd} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="end">
          <Menu.Popup className={menuStyles.popup}>
            <Menu.RadioGroup
              value={facets.status}
              onValueChange={(value) => {
                onChange({ ...facets, status: value as StatusFacet });
              }}
            >
              <Menu.GroupLabel className={menuStyles.groupLabel}>
                {t('web:reports.surveys.filters.statusLabel')}
              </Menu.GroupLabel>
              {STATUSES.map((status) => (
                <Menu.RadioItem key={status} className={menuStyles.item} value={status}>
                  {/* A tick AND the `aria-checked` Base UI writes on the item: the mark is a second
                      * channel rather than the only one, and `.mark:empty` draws the
                      * unticked outline off the mark's own text. */}
                    <span className={menuStyles.mark} aria-hidden="true">
                      {facets.status === status ? '✓' : ''}
                    </span>
                  <span className={menuStyles.itemLabel}>
                    {t(`web:reports.surveys.filters.${STATUS_KEY[status]}`)}
                  </span>
                </Menu.RadioItem>
              ))}
            </Menu.RadioGroup>

            <Menu.Separator className={menuStyles.separator} />

            <Menu.RadioGroup
              value={facets.cadence}
              onValueChange={(value) => {
                onChange({ ...facets, cadence: value as CadenceFacet });
              }}
            >
              <Menu.GroupLabel className={menuStyles.groupLabel}>
                {t('web:reports.surveys.filters.cadenceLabel')}
              </Menu.GroupLabel>
              {CADENCES.map((cadence) => (
                <Menu.RadioItem key={cadence} className={menuStyles.item} value={cadence}>
                  {/* A tick AND the `aria-checked` Base UI writes on the item: the mark is a second
                      * channel rather than the only one, and `.mark:empty` draws the
                      * unticked outline off the mark's own text. */}
                    <span className={menuStyles.mark} aria-hidden="true">
                      {facets.cadence === cadence ? '✓' : ''}
                    </span>
                  <span className={menuStyles.itemLabel}>
                    {t(`web:reports.surveys.filters.${CADENCE_KEY[cadence]}`)}
                  </span>
                </Menu.RadioItem>
              ))}
            </Menu.RadioGroup>

            {/* The client group is drawn only when somebody actually holds a form. A heading over
              * one entry reading „Każdy" is a control whose every setting means the same thing. */}
            {clients.length === 0 ? null : (
              <>
                <Menu.Separator className={menuStyles.separator} />
                <Menu.RadioGroup
                  value={facets.clientId === null ? ANY_CLIENT : String(facets.clientId)}
                  onValueChange={(value) => {
                    onChange({
                      ...facets,
                      clientId: value === ANY_CLIENT ? null : Number(value),
                    });
                  }}
                >
                  <Menu.GroupLabel className={menuStyles.groupLabel}>
                    {t('web:reports.surveys.filters.clientLabel')}
                  </Menu.GroupLabel>
                  <Menu.RadioItem className={menuStyles.item} value={ANY_CLIENT}>
                    {/* A tick AND the `aria-checked` Base UI writes on the item: the mark is a second
                      * channel rather than the only one, and `.mark:empty` draws the
                      * unticked outline off the mark's own text. */}
                    <span className={menuStyles.mark} aria-hidden="true">
                      {facets.clientId === null ? '✓' : ''}
                    </span>
                    <span className={menuStyles.itemLabel}>
                      {t('web:reports.surveys.filters.clientAll')}
                    </span>
                  </Menu.RadioItem>
                  {clients.map((client) => (
                    <Menu.RadioItem
                      key={client.id}
                      className={menuStyles.item}
                      value={String(client.id)}
                    >
                      {/* A tick AND the `aria-checked` Base UI writes on the item: the mark is a second
                      * channel rather than the only one, and `.mark:empty` draws the
                      * unticked outline off the mark's own text. */}
                    <span className={menuStyles.mark} aria-hidden="true">
                      {facets.clientId === client.id ? '✓' : ''}
                    </span>
                      <span className={menuStyles.itemLabel}>{client.name}</span>
                    </Menu.RadioItem>
                  ))}
                </Menu.RadioGroup>
              </>
            )}

            {/* An ACT, not a choice: it is something the menu DOES rather than a value the control
              * can hold, so it is a plain item under a separator and it closes the menu. The phrase
              * in the search box is the trainer's own and is left alone — the button says filters. */}
            {active === 0 ? null : (
              <>
                <Menu.Separator className={menuStyles.separator} />
                <Menu.Item
                  className={menuStyles.item}
                  onClick={() => {
                    onChange({ ...NO_SURVEY_FACETS, query: facets.query });
                  }}
                >
                  <span className={menuStyles.itemLabel}>
                    {t('web:reports.surveys.filters.clear')}
                  </span>
                </Menu.Item>
              </>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
